<div class="public-lead-form-container" [attr.dir]="direction">
    <!-- Loading State -->
    <div class="loading-state" *ngIf="loading">
        <mat-spinner diameter="50"></mat-spinner>
        <p>{{ translatePublicForm('loading') }}</p>
    </div>

    <!-- Error State -->
    <div class="error-state" *ngIf="error && !loading">
        <mat-icon class="error-icon">error_outline</mat-icon>
        <h2>{{ translatePublicForm('errors.oops') }}</h2>
        <p>{{ error }}</p>
    </div>

    <!-- Form Disabled State -->
    <div class="disabled-state" *ngIf="!loading && !error && formConfig && !formConfig.isEnabled">
        <mat-icon class="disabled-icon">block</mat-icon>
        <h2>{{ translatePublicForm('states.not-available-title') }}</h2>
        <p>{{ translatePublicForm('states.not-available-message') }}</p>
    </div>

    <!-- Success State -->
    <div class="success-state" *ngIf="submitted">
        <mat-icon class="success-icon">check_circle</mat-icon>
        <h2>{{ translatePublicForm('success.title') }}</h2>
        <p>{{ translatePublicForm('success.message') }}</p>
    </div>

    <!-- Lead Form -->
    <div class="form-page" *ngIf="!loading && !error && formConfig?.isEnabled && !submitted">

        <!-- A refused submission, shown above the form the person is still filling in. Distinct
             from `error`, which is the fatal load-failure panel that replaces the page. -->
        <div class="submit-error" *ngIf="submitError">
            <mat-icon class="error-icon">error_outline</mat-icon>
            <span>{{ submitError }}</span>
        </div>
        <p class="welcome-message">{{ translatePublicForm('welcome') }}</p>

        <div class="form-card">
            <div class="form-header">
                <div class="form-header-row">
                    <h2 class="form-title">{{ translatePublicForm('title') }}</h2>
                    <language-selector [forceEnabled]="true" [useGlobeIcon]="true"></language-selector>
                </div>
                <p class="form-subtitle">{{ translatePublicForm('subtitle') }}</p>
            </div>

            <form [formGroup]="leadForm" (ngSubmit)="onSubmit()" class="lead-form">
            <!-- Adult or Child. No family picker here: an anonymous submitter must not be able to
                 search a tenant's families, so they type the parent's address and the server links
                 the family. -->
            <div class="lead-type-selection">
                <label class="field-label">
                    {{ 'portal.leads.form.adult-or-child' | translate }}
                </label>
                <mat-radio-group
                    [value]="isChild ? 'child' : 'adult'"
                    (change)="onLeadTypeChange($event.value === 'child')"
                >
                    <mat-radio-button value="adult">
                        {{ 'portal.clients.components.add-edit-client.adult' | translate }}
                    </mat-radio-button>
                    <mat-radio-button value="child">
                        {{ 'portal.clients.components.add-edit-client.child' | translate }}
                    </mat-radio-button>
                </mat-radio-group>
            </div>

                <!-- Name Fields -->
                <div class="form-row" *ngIf="isFieldVisible('name')">
                    <div class="field-group">
                        <label class="field-label">
                            {{ getFieldLabel('firstName') }} <span class="required" *ngIf="isFieldRequired('name')">*</span>
                        </label>
                        <input
                            type="text"
                            class="field-input"
                            formControlName="firstName"
                            [placeholder]="translatePublicForm('placeholders.firstName')"
                            [class.error]="(leadForm.get('firstName')?.invalid && leadForm.get('firstName')?.touched) || hasFieldError('firstName')">
                        <span class="field-error" *ngIf="leadForm.get('firstName')?.hasError('required') && leadForm.get('firstName')?.touched">
                            {{ translatePublicForm('validation.first-name-required') }}
                        </span>
                        <span class="field-error" *ngIf="hasFieldError('firstName')">
                            {{ getFieldError('firstName') }}
                        </span>
                    </div>

                    <div class="field-group">
                        <label class="field-label">
                            {{ getFieldLabel('lastName') }} <span class="required" *ngIf="isFieldRequired('name')">*</span>
                        </label>
                        <input
                            type="text"
                            class="field-input"
                            formControlName="lastName"
                            [placeholder]="translatePublicForm('placeholders.lastName')"
                            [class.error]="(leadForm.get('lastName')?.invalid && leadForm.get('lastName')?.touched) || hasFieldError('lastName')">
                        <span class="field-error" *ngIf="leadForm.get('lastName')?.hasError('required') && leadForm.get('lastName')?.touched">
                            {{ translatePublicForm('validation.last-name-required') }}
                        </span>
                        <span class="field-error" *ngIf="hasFieldError('lastName')">
                            {{ getFieldError('lastName') }}
                        </span>
                    </div>
                </div>

                <!-- Email Field -->
                <div class="field-group" *ngIf="isFieldVisible('email')">
                    <label class="field-label">
                        {{ getFieldLabel('email') }} <span class="required" *ngIf="isFieldRequired('email')">*</span>
                    </label>
                    <input
                        type="email"
                        class="field-input"
                        formControlName="email"
                        (blur)="sanitizeEmailField('email')"
                        [placeholder]="translatePublicForm('placeholders.email')"
                        [class.error]="(leadForm.get('email')?.invalid && leadForm.get('email')?.touched) || hasFieldError('email')">
                    <span class="field-error" *ngIf="leadForm.get('email')?.hasError('required') && leadForm.get('email')?.touched && !hasFieldError('email')">
                        {{ translatePublicForm('validation.email-required') }}
                    </span>
                    <span class="field-error" *ngIf="leadForm.get('email')?.hasError('email') && leadForm.get('email')?.touched && !hasFieldError('email')">
                        {{ translatePublicForm('validation.email') }}
                    </span>
                    <span class="field-error" *ngIf="hasFieldError('email')">
                        {{ getFieldError('email') }}
                    </span>
                </div>

                <!-- Phone Field -->
                <div class="field-group" *ngIf="isFieldVisible('phoneNumber')">
                    <label class="field-label">
                        {{ getFieldLabel('phoneNumber') }} <span class="required" *ngIf="isFieldRequired('phoneNumber')">*</span>
                    </label>
                    <input
                        type="tel"
                        class="field-input"
                        formControlName="phoneNumber"
                        [placeholder]="translatePublicForm('placeholders.phoneNumber')"
                        [class.error]="(leadForm.get('phoneNumber')?.invalid && leadForm.get('phoneNumber')?.touched) || hasFieldError('phoneNumber')">
                    <span class="field-error" *ngIf="leadForm.get('phoneNumber')?.hasError('required') && leadForm.get('phoneNumber')?.touched && !hasFieldError('phoneNumber')">
                        {{ translatePublicForm('validation.phone-required') }}
                    </span>
                    <span class="field-error" *ngIf="hasFieldError('phoneNumber')">
                        {{ getFieldError('phoneNumber') }}
                    </span>
                </div>

                <!-- How Did You Hear Field -->
                <div class="field-group" *ngIf="isFieldVisible('howDidYouHear')">
                    <label class="field-label">
                        {{ getFieldLabel('howDidYouHear') }} <span class="required" *ngIf="isFieldRequired('howDidYouHear')">*</span>
                    </label>
                    <select
                        class="field-input field-select"
                        formControlName="howDidYouHear"
                        (change)="onHowDidYouHearChange($any($event.target).value)"
                        [class.error]="(leadForm.get('howDidYouHear')?.invalid && leadForm.get('howDidYouHear')?.touched) || hasFieldError('howDidYouHear')">
                        <option value="">{{ translatePublicForm('placeholders.select-option') }}</option>
                        <option *ngFor="let option of howDidYouHearOptions" [value]="option.value">
                            {{ option.displayName }}
                        </option>
                    </select>
                    <span class="field-error" *ngIf="leadForm.get('howDidYouHear')?.hasError('required') && leadForm.get('howDidYouHear')?.touched && !hasFieldError('howDidYouHear')">
                        {{ translatePublicForm('validation.required') }}
                    </span>
                    <span class="field-error" *ngIf="hasFieldError('howDidYouHear')">
                        {{ getFieldError('howDidYouHear') }}
                    </span>
                </div>

                <!-- How Did You Hear Other Field -->
                <div class="field-group" *ngIf="showOtherInput">
                    <label class="field-label">
                        {{ getFieldLabel('howDidYouHearOther') }}
                    </label>
                    <input
                        type="text"
                        class="field-input"
                        formControlName="howDidYouHearOther"
                        [placeholder]="translatePublicForm('placeholders.howDidYouHearOther')"
                        [class.error]="hasFieldError('howDidYouHearOther')">
                    <span class="field-error" *ngIf="hasFieldError('howDidYouHearOther')">
                        {{ getFieldError('howDidYouHearOther') }}
                    </span>
                </div>

                <!-- Notes Field -->
                <div class="field-group" *ngIf="isFieldVisible('notes')">
                    <label class="field-label">
                        {{ getFieldLabel('notes') }} <span class="required" *ngIf="isFieldRequired('notes')">*</span>
                    </label>
                    <textarea
                        class="field-input field-textarea"
                        formControlName="notes"
                        rows="4"
                        [placeholder]="translatePublicForm('placeholders.notes')"
                        [class.error]="leadForm.get('notes')?.invalid && leadForm.get('notes')?.touched"></textarea>
                    <span class="field-error" *ngIf="leadForm.get('notes')?.hasError('required') && leadForm.get('notes')?.touched">
                        {{ translatePublicForm('validation.required') }}
                    </span>
                </div>

                <!-- Additional Fields (dynamically rendered) -->
                <ng-container *ngFor="let fieldKey of getVisibleAdditionalFields()">
                    <div class="field-group">
                        <label class="field-label">
                            {{ getFieldLabel(fieldKey) }} <span class="required" *ngIf="getFieldConfig(fieldKey)?.required">*</span>
                        </label>

                        <!-- School dropdown (FK-based single select) -->
                        <select *ngIf="fieldKey === 'school' && schoolOptions.length > 0"
                            class="field-input field-select"
                            formControlName="idSchool"
                            [class.error]="leadForm.get('idSchool')?.invalid && leadForm.get('idSchool')?.touched">
                            <option value="">{{ translatePublicForm('placeholders.school') }}</option>
                            <option *ngFor="let option of schoolOptions" [value]="option.id">
                                {{ option.name }}
                            </option>
                        </select>

                        <!-- Health Conditions multi-select (FK-based) -->
                        <mat-form-field *ngIf="fieldKey === 'healthCond' && healthConditionOptions.length > 0" appearance="outline" class="field-mat-select">
                            <mat-select formControlName="healthConditionIds" multiple [placeholder]="translatePublicForm('placeholders.healthCond')">
                                <mat-option *ngFor="let option of healthConditionOptions" [value]="option.id">
                                    {{ option.name }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <!-- Injuries multi-select (FK-based) -->
                        <mat-form-field *ngIf="fieldKey === 'injuries' && injuryOptions.length > 0" appearance="outline" class="field-mat-select">
                            <mat-select formControlName="injuryIds" multiple [placeholder]="translatePublicForm('placeholders.injuries')">
                                <mat-option *ngFor="let option of injuryOptions" [value]="option.id">
                                    {{ option.name }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <!-- Date field -->
                        <input *ngIf="getFieldConfig(fieldKey)?.fieldType === 'date'"
                            type="date"
                            class="field-input"
                            [formControlName]="fieldKey"
                            [class.error]="leadForm.get(fieldKey)?.invalid && leadForm.get(fieldKey)?.touched">

                        <!-- Dropdown field -->
                        <select *ngIf="getFieldConfig(fieldKey)?.fieldType === 'dropdown' && !isFkDropdownField(fieldKey)"
                            class="field-input field-select"
                            [formControlName]="fieldKey"
                            [class.error]="leadForm.get(fieldKey)?.invalid && leadForm.get(fieldKey)?.touched">
                            <option value="">{{ translatePublicForm('placeholders.select-option') }}</option>
                            <option *ngFor="let option of getFieldConfig(fieldKey)?.options" [value]="option">
                                {{ option }}
                            </option>
                        </select>

                        <!-- Email field -->
                        <input *ngIf="getFieldConfig(fieldKey)?.fieldType === 'email'"
                            type="email"
                            class="field-input"
                            [formControlName]="fieldKey"
                            [placeholder]="getFieldPlaceholder(fieldKey)"
                            (blur)="sanitizeEmailField(fieldKey)"
                            [class.error]="leadForm.get(fieldKey)?.invalid && leadForm.get(fieldKey)?.touched">

                        <!-- Phone field -->
                        <input *ngIf="getFieldConfig(fieldKey)?.fieldType === 'phone'"
                            type="tel"
                            class="field-input"
                            [formControlName]="fieldKey"
                            [placeholder]="getFieldPlaceholder(fieldKey)"
                            [class.error]="leadForm.get(fieldKey)?.invalid && leadForm.get(fieldKey)?.touched">

                        <!-- Default text field (only if not FK dropdown field) -->
                        <input *ngIf="(!getFieldConfig(fieldKey)?.fieldType || getFieldConfig(fieldKey)?.fieldType === 'text') && !isFkDropdownField(fieldKey)"
                            type="text"
                            class="field-input"
                            [formControlName]="fieldKey"
                            [placeholder]="getFieldPlaceholder(fieldKey)"
                            [class.error]="leadForm.get(fieldKey)?.invalid && leadForm.get(fieldKey)?.touched">

                        <span class="field-error" *ngIf="leadForm.get(fieldKey)?.hasError('required') && leadForm.get(fieldKey)?.touched && !hasFieldError(fieldKey)">
                            {{ translatePublicForm('validation.required') }}
                        </span>
                        <span class="field-error" *ngIf="leadForm.get(fieldKey)?.hasError('email') && leadForm.get(fieldKey)?.touched && !hasFieldError(fieldKey)">
                            {{ translatePublicForm('validation.email') }}
                        </span>
                        <span class="field-error" *ngIf="hasFieldError(fieldKey)">
                            {{ getFieldError(fieldKey) }}
                        </span>
                    </div>
                </ng-container>

                <!-- Buttons -->
                <div class="form-actions">
                    <button
                        type="submit"
                        class="btn-submit"
                        [ngStyle]="{'background-color': primaryColor}"
                        [disabled]="submitting">
                        <mat-spinner *ngIf="submitting" diameter="20"></mat-spinner>
                        <span *ngIf="!submitting">{{ translatePublicForm('actions.submit') }}</span>
                    </button>
                    <button
                        type="button"
                        class="btn-clear"
                        (click)="leadForm.reset()"
                        [disabled]="submitting">
                        {{ translatePublicForm('actions.clear') }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</div>
